import {AxiosError} from 'axios';
import {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {CredentialType} from 'web-library/models/AccountManagementConfiguration';
import {GenericErrorResponse} from '../../../api';
import {CandidateProfileFormData, CandidateProfileIsDisableInputs} from '../../../models';
import {getNullIfEmptyString} from '../../../utils/StringUtils';
import {IExamMasterCodeItem} from '../../../api/user/model';
import {
  CreateCandidateRequestData,
  usePostCreateCandidateNew,
} from '../../../api-refactored/user/usePostCreateCandidateNew';
import {UseCustomMutationReturn} from '../../../api-refactored/utils/useCustomMutation';
import {convertDisplayNameToPrStatus} from './usePrCitizenshipForm';

export const ID_TYPE_MENU_ITEMS = [
  {key: 'I', value: 'I', children: 'NRIC/FIN'},
  {key: 'P', value: 'P', children: 'Passport No.'},
];

export const getIExamDropdownItemLabel = (option: IExamMasterCodeItem) =>
    option.codeValueDescription;

export const getIExamMasterCodeSelected = (
    option: IExamMasterCodeItem,
    value: IExamMasterCodeItem
) => option.codeId === value?.codeId;

interface CreateCandidateFormDefaultValue extends Partial<CandidateProfileFormData> {
}

export interface CreateCandidateFormLocationStateFormData {
  formDataDefaultValues: CreateCandidateFormDefaultValue;
  formDataIsDisabled: CandidateProfileIsDisableInputs;
}

export interface CreateCandidateFormLocationState {
  formData?: CreateCandidateFormLocationStateFormData;
  credentialType?: CredentialType;
  hasMyInfoProfile?: boolean;
}

export interface useCreateCandidateFormReturn {
  /**
   * Function to trigger api to create candidate
   */
  createCandidate: (formData: CandidateProfileFormData, credentialType?: CredentialType) => void;
  /**
   * Extra handling error message for the create candidate api
   */
  submitError: string | undefined;
  /**
   * React query mutation object
   */
  createCandidateMutation: UseCustomMutationReturn<
      any,
      AxiosError<GenericErrorResponse>,
      CreateCandidateRequestData,
      unknown
  >;
}

/**
 * A hook to handle candidate creation logic
 *
 * @param onSuccessCallback callback after create candidate API returns success response
 * @param formDataDefaultValues default values that should be populated to create candidate form
 */
export const useCreateCandidateForm = (
    onSuccessCallback: () => void
): useCreateCandidateFormReturn => {
  const [submitError, setSubmitError] = useState<string>();
  const {t} = useTranslation();

  const createCandidateMutation = usePostCreateCandidateNew({
    onSuccess: () => {
      onSuccessCallback();
    },
    onError: (err) => createCandidateFailCallback(err),
  });

  const createCandidateFailCallback = (err: AxiosError<GenericErrorResponse>) => {
    const applicationCode: string = err.response?.data.code!;
    switch (err.response?.status) {
      case undefined:
        setSubmitError('');
        break;
      case 400:
        if (applicationCode === '1002') {
          setSubmitError('Missing required information.');
        } else if (applicationCode === '1202') {
          setSubmitError('Candidate account with the same NRIC/FIN/Passport No. has been used');
        } else if (applicationCode === '1214') {
          setSubmitError(t('profile->>CreateCandidateForm.InvalidPrStatus'));
        } else {
          setSubmitError(err.response?.data.message);
        }
        break;
      case 403:
        if (applicationCode === '1007') {
          setSubmitError(err.response?.data.message);
        } else {
          setSubmitError(t('components->>Error.DefaultErrorMsg'));
        }
        break;
      default:
        setSubmitError('Unknown error. Please try again.');
        break;
    }
  };

  const createCandidate = (formData: CandidateProfileFormData, credentialType?: CredentialType) => {
    setSubmitError(undefined);
    const data: CreateCandidateRequestData = {
      user: {
        name: getNullIfEmptyString(formData.name),
        email: getNullIfEmptyString(formData.email),
        countryCode: parseInt(formData.mobileCountryCode?.codeValue || '', 10) || null,
        contactNumber: getNullIfEmptyString(formData.mobileContactNumber),
        dateOfBirth: formData.dateOfBirth,
        enabled: false,
      },
      candidateType: 'P',
      candidateId: getNullIfEmptyString(formData.NricFin),
      userIdType: formData.IdType === 'I' ? 'NRIC_OR_FIN' : 'PASSPORT',
      organisationId: formData?.school?.id ?? null,
      organisationUuid: formData?.school?.uuid.toString(),
      hanyuPinyinName: '',
      chineseName: null,
      gender: getNullIfEmptyString(formData?.gender?.codeValueDescription),
      dateOfBirth: formData.dateOfBirth,
      race: getNullIfEmptyString(formData?.race?.codeValueDescription),
      secondRace: getNullIfEmptyString(formData.secondaryRace),
      nationality: getNullIfEmptyString(formData?.nationality?.codeValueDescription),
      nationalityIssueDate: formData.nationalityIssueDate,
      citizenship: getNullIfEmptyString(formData?.citizenship?.codeValueDescription),
      localAddressBlockNumber: getNullIfEmptyString(formData.houseOrBlkNo),
      localAddressUnitNumber: getNullIfEmptyString(formData.unit),
      localAddressFloorNumber: getNullIfEmptyString(formData.floor),
      localAddressStreetName: getNullIfEmptyString(formData.streetName),
      localAddressBuildingName: getNullIfEmptyString(formData.building),
      localAddressPostalCode: getNullIfEmptyString(formData.postalCode),
      isLocalAddressPreferred: formData.preferredAddress === 'L',
      isMobileNumberPreferred: formData.preferredTelephoneOrMobile === 'M',
      prStatus: convertDisplayNameToPrStatus(
          getNullIfEmptyString(formData?.prStatus?.codeValueDescription)
      ),
      occupation: formData.occupation,
      foreignAddress1: getNullIfEmptyString(formData.foreignAddress1),
      foreignAddress2: getNullIfEmptyString(formData.foreignAddress2),
      foreignAddress3: getNullIfEmptyString(formData.foreignAddress3),
      nextOfKinName: getNullIfEmptyString(formData.nextOfKinName),
      nextOfKinRelationship: getNullIfEmptyString(
          formData?.nextOfKinRelationshipToCandidate?.codeValueDescription
      ),
      iexamsId: '',
      telephoneNumberCountryCode:
          parseInt(formData.telephoneCountryCode?.codeValue || '', 10) || null,
      telephoneNumber: getNullIfEmptyString(formData.telephoneContactNumber),
      nextOfKinContactNumberCountryCode:
          parseInt(formData.nextOfKinCountryCode?.codeValue || '', 10) || null,
      nextOfKinContactNumber: getNullIfEmptyString(formData.nextOfKinContactNumber),
      credentialType: credentialType || null,
      activationRequired: true,
    };

    createCandidateMutation.mutate(data);
  };

  return {
    createCandidate,
    submitError,
    createCandidateMutation,
  };
};
